import { c as _c } from "react/compiler-runtime";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE, MAX_VIDEO_FILE_SIZE } from "@/constants";
import { Badge } from "@/components/ui/badge";
import { Body } from "@/components/ui/text";
import { FileText, Upload } from "lucide-react";
import Image from "next/image";
import { useCallback, useState } from "react";
import { ErrorCode, useDropzone } from "react-dropzone";
const ERROR_CODE_TO_MESSAGE = {
  [ErrorCode.FileInvalidType]: "File type not allowed",
  [ErrorCode.FileTooLarge]: "The file you are trying to upload is too large. The maximum file size is: 50MB for images/documents, 500MB for videos.",
  [ErrorCode.FileTooSmall]: "File size too small",
  [ErrorCode.TooManyFiles]: "Too many files"
};
type FileWithPreview = {
  file: File; // keep original file completely untouched
  preview?: string;
  id: string;
  uploadProgress?: number;
  status: "pending" | "uploading" | "success" | "error";
  type: string;
  size: number;
};
type FileUploadFormProps = {
  onFileUpload?: (file: FileWithPreview) => Promise<void>;
  maxFiles?: number;
  maxSize?: number; // in bytes
  acceptedFileTypes?: string[];
};
export function FileUploadForm(t0) {
  const $ = _c(34);
  const {
    onFileUpload,
    maxFiles: t1,
    acceptedFileTypes: t2
  } = t0;
  const maxFiles = t1 === undefined ? 10 : t1;
  const acceptedFileTypes = t2 === undefined ? ACCEPTED_FILE_TYPES : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [filesWithPreview, setFiles] = useState(t3);
  let t4;
  if ($[1] !== onFileUpload) {
    t4 = async file => {
      if (!onFileUpload) {
        return;
      }
      ;
      try {
        setFiles(prev_0 => prev_0.map(f_0 => f_0.id === file.id ? {
          ...f_0,
          status: "uploading" as const,
          uploadProgress: 0
        } : f_0));
        const progressInterval = setInterval(() => {
          setFiles(prev_1 => prev_1.map(f_1 => {
            if (f_1.id === file.id && f_1.uploadProgress !== undefined && f_1.uploadProgress < 90) {
              return {
                ...f_1,
                uploadProgress: f_1.uploadProgress + 10
              };
            }
            return f_1;
          }));
        }, 200);
        await onFileUpload(file);
        clearInterval(progressInterval);
        setFiles(prev_2 => prev_2.map(f_2 => f_2.id === file.id ? {
          ...f_2,
          status: "success" as const,
          uploadProgress: 100
        } : f_2));
      } catch (t5) {
        setFiles(prev => prev.map(f => f.id === file.id ? {
          ...f,
          status: "error" as const
        } : f));
      }
    };
    $[1] = onFileUpload;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const uploadSingleFile = t4;
  const validator = _temp;
  let t5;
  if ($[3] !== filesWithPreview || $[4] !== maxFiles || $[5] !== onFileUpload || $[6] !== uploadSingleFile) {
    t5 = async acceptedFiles => {
      const newFiles = acceptedFiles.map(_temp2);
      const updatedFiles = [...filesWithPreview, ...newFiles].slice(0, maxFiles);
      setFiles(updatedFiles);
      if (onFileUpload) {
        for (const file_2 of newFiles) {
          await uploadSingleFile(file_2);
        }
      }
    };
    $[3] = filesWithPreview;
    $[4] = maxFiles;
    $[5] = onFileUpload;
    $[6] = uploadSingleFile;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const onDrop = t5;
  let t6;
  if ($[8] !== acceptedFileTypes) {
    t6 = acceptedFileTypes.reduce(_temp3, {} as Record<string, string[]>);
    $[8] = acceptedFileTypes;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== maxFiles || $[11] !== onDrop || $[12] !== t6) {
    t7 = {
      onDrop,
      maxFiles,
      validator,
      accept: t6
    };
    $[10] = maxFiles;
    $[11] = onDrop;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections
  } = useDropzone(t7);
  const getFileIcon = _temp4;
  const getStatusBadge = _temp5;
  const formatFileSize = _temp6;
  let t8;
  if ($[14] !== fileRejections) {
    t8 = fileRejections.length > 0 && <div className="flex flex-col gap-2">{fileRejections.map(fileRejection => <Body key={fileRejection.file.name} className="text-status-error"><>{fileRejection.errors[0]?.message || ERROR_CODE_TO_MESSAGE[fileRejection.errors[0]?.code as ErrorCode]}{fileRejections.length > 1 && <>{" "}- <em>{fileRejection.file.name}</em></>}</></Body>)}</div>;
    $[14] = fileRejections;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== getRootProps) {
    t9 = getRootProps();
    $[16] = getRootProps;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = `
            flex flex-col gap-1 items-center justify-center border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive && !isDragReject ? "border-status-info bg-status-info-lighten-3" : ""}
            ${isDragReject ? "border-status-error bg-status-error-lighten-3" : ""}
            ${!isDragActive && !isDragReject ? "border-border-muted hover:border-border-muted-hover" : ""}
          `;
  let t11;
  if ($[18] !== getInputProps) {
    t11 = getInputProps();
    $[18] = getInputProps;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = <input {...t11} />;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Upload className="h-8 w-8 text-foreground-muted mb-4" />;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="space-y-2"><Body className="text-md font-medium text-foreground">Click or drag files here to upload</Body><Body className="text-xs text-foreground-muted">JPG, JPEG, GIF, PNG, PDF, MP4, MOV formats allowed. Maximum file size: 50MB for images/documents, 500MB for videos</Body></div>;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t10 || $[25] !== t12 || $[26] !== t9) {
    t15 = <div {...t9} className={t10}>{t12}{t13}{t14}</div>;
    $[24] = t10;
    $[25] = t12;
    $[26] = t9;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== filesWithPreview) {
    t16 = filesWithPreview.length > 0 && <div className="flex flex-col gap-2">{filesWithPreview.filter(_temp7).map(fileWithPreview_0 => <div key={fileWithPreview_0.id} className="flex items-center gap-3 p-3"><div className="flex-shrink-0">{fileWithPreview_0.preview && fileWithPreview_0.file.type?.startsWith("image/") ? <Image src={fileWithPreview_0.preview} alt={fileWithPreview_0.file.name || "File Preview"} className="w-10 h-10 object-cover rounded" width={40} height={40} /> : <div className="w-10 h-10 bg-background-muted rounded flex items-center justify-center">{getFileIcon(fileWithPreview_0)}</div>}</div><div className="flex-1 min-w-0"><div className="flex items-center gap-2"><Body className="font-medium truncate">{fileWithPreview_0.file.name}</Body>{getStatusBadge(fileWithPreview_0.status)}</div><Body className="text-sm text-foreground-muted">{formatFileSize(fileWithPreview_0.file.size)}</Body>{fileWithPreview_0.status === "uploading" && fileWithPreview_0.uploadProgress !== undefined && <div className="mt-2"><div className="w-full bg-border-muted rounded-full h-2"><div className="bg-status-info h-2 rounded-full transition-all duration-300" style={{
                width: `${fileWithPreview_0.uploadProgress}%`
              }} /></div><Body className="text-xs text-foreground-muted mt-1">{fileWithPreview_0.uploadProgress}%</Body></div>}</div></div>)}</div>;
    $[28] = filesWithPreview;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t15 || $[31] !== t16 || $[32] !== t8) {
    t17 = <div className="flex flex-col gap-6">{t8}{t15}{t16}</div>;
    $[30] = t15;
    $[31] = t16;
    $[32] = t8;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  return t17;
}
function _temp7(file_4) {
  return file_4.status !== "success";
}
function _temp6(bytes) {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i];
}
function _temp5(status) {
  switch (status) {
    case "pending":
      {
        return <Badge intent="neutral">Pending</Badge>;
      }
    case "uploading":
      {
        return <Badge intent="callout">Uploading</Badge>;
      }
    case "success":
      {
        return <Badge intent="success">Success</Badge>;
      }
    case "error":
      {
        return <Badge intent="danger">Error</Badge>;
      }
  }
}
function _temp4(file_3) {
  if (file_3.file.type?.startsWith("image/")) {
    return <Image src={file_3.preview || ""} alt={file_3.file.name || "File Preview"} width={24} height={24} className="w-6 h-6 object-cover rounded" />;
  }
  return <FileText className="w-6 h-6 text-foreground-muted" />;
}
function _temp3(acc, type) {
  acc[type] = [];
  return acc;
}
function _temp2(file_1) {
  const fileWithPreview = {
    file: file_1,
    id: Math.random().toString(36).substr(2, 9),
    status: "pending",
    preview: file_1.type.startsWith("image/") ? URL.createObjectURL(file_1) : undefined,
    type: file_1.type,
    size: file_1.size
  };
  return fileWithPreview;
}
function _temp(file_0) {
  const isVideo = file_0.type === "video/mp4" || file_0.type === "video/quicktime";
  const maxAllowedSize = isVideo ? MAX_VIDEO_FILE_SIZE : MAX_FILE_SIZE;
  const maxSizeLabel = isVideo ? "500MB" : "50MB";
  if (file_0.size > maxAllowedSize) {
    return {
      code: "file-too-large",
      message: `File size is too large. ${maxSizeLabel} maximum for ${isVideo ? "videos" : "images/documents"}.`
    };
  }
  return null;
}